import React, { useState } from "react";
import { setTopicCategory } from "../api";

/**
 * Admin-only "Category ▾" picker shown in a topic's header (regular topics
 * and grammar lessons alike). Moves the topic between sidebar categories via
 * POST /api/topics/<id>/category/ and reports the change to the parent so the
 * sidebar and any cached topic object stay in sync.
 *
 * Renders nothing when there are no categories to choose from.
 */
export default function TopicCategoryPicker({ topicId, categoryId, categories = [], onChanged, className = "" }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  if (!categories.length) return null;

  async function handleSelect(rawValue) {
    const next = rawValue === "" ? null : Number(rawValue);
    setSaving(true);
    setError(null);
    try {
      await setTopicCategory(topicId, next);
      if (onChanged) onChanged(topicId, next);
    } catch (e) {
      setError(e.message || "Could not move the topic.");
    } finally {
      setSaving(false);
    }
  }

  const value = categoryId != null && categories.some((c) => c.id === categoryId) ? String(categoryId) : "";

  return (
    <label
      className={`shrink-0 flex items-center gap-1.5 text-xs ${className}`}
      style={{ color: "var(--parchment-dim)" }}
      title="Sidebar category this topic is listed under"
    >
      <span>Category</span>
      <select
        value={value}
        onChange={(e) => handleSelect(e.target.value)}
        disabled={saving}
        className="text-xs px-2 py-1 rounded-md border bg-transparent disabled:opacity-50"
        style={{ color: "var(--gold-bright)", borderColor: "var(--panel-line)" }}
        aria-label="Topic category"
        data-testid="topic-category-select"
      >
        <option value="">No category</option>
        {categories.map((c) => (
          <option key={c.id} value={String(c.id)}>{c.name}</option>
        ))}
      </select>
      {error && <span style={{ color: "var(--tag-rose)" }}>{error}</span>}
    </label>
  );
}
